import { useEffect, useRef, useState } from 'react';
import { BaseItem, InternalData, StickyGroupBand } from '../../VirtualizedTable.types';
import { STICKY_GROUP_ROW_HEIGHT } from '../../virtualizedTable.styles';
import {
  areBandsEqual,
  buildRowsSignature,
  indexStickyGroups,
  resolveStickyBands,
} from './stickyGroupBands.utils';
import { collectRowTops, findStickyGroupTable, measureBandHeight } from './stickyGroupTable.utils';

interface UseStickyGroupBandsParams<T extends BaseItem> {
  data: InternalData<T>[];
  isEnabled: boolean;
  overlay: HTMLElement | null;
}

interface StickyGroupMeasurement<T extends BaseItem> {
  overlay: HTMLElement;
  bands: StickyGroupBand<T>[];
  bandHeight: number;
}

const EMPTY_BANDS: never[] = [];

const useStickyGroupBands = <T extends BaseItem>({
  data,
  isEnabled,
  overlay,
}: UseStickyGroupBandsParams<T>) => {
  const [measurement, setMeasurement] = useState<StickyGroupMeasurement<T> | null>(null);
  const dataRef = useRef(data);
  const scheduleRef = useRef<(() => void) | null>(null);
  const rowsSignature = isEnabled ? buildRowsSignature(data) : '';

  useEffect(() => {
    dataRef.current = data;
    scheduleRef.current?.();
  }, [data]);

  useEffect(() => {
    const table = rowsSignature && overlay ? findStickyGroupTable(overlay) : null;

    if (!overlay || !table) {
      return;
    }

    const index = indexStickyGroups(dataRef.current);
    let frame = 0;

    const measure = () => {
      frame = 0;
      const rows = dataRef.current;
      const rowTops = collectRowTops(table);
      const firstRenderedPosition = rows.findIndex((row) => rowTops.has(row.id));
      const bandHeight = measureBandHeight(overlay);
      const rowsById = new Map(rows.map((row) => [row.id, row]));
      const bands = resolveStickyBands<T>({
        index,
        rowTops,
        firstRenderedPosition: firstRenderedPosition < 0 ? rows.length : firstRenderedPosition,
        chromeTop: overlay.getBoundingClientRect().top,
        bandHeight,
      }).map((band) => {
        const currentRow = rowsById.get(band.row.id);

        return currentRow ? { ...band, row: currentRow } : band;
      });

      setMeasurement((current) =>
        current?.overlay === overlay &&
        current.bandHeight === bandHeight &&
        areBandsEqual(current.bands, bands)
          ? current
          : { overlay, bands, bandHeight },
      );
    };

    const scheduleMeasure = () => {
      if (frame) {
        return;
      }

      frame = requestAnimationFrame(measure);
    };

    const rowsObserver = new MutationObserver(scheduleMeasure);
    rowsObserver.observe(table, { childList: true, subtree: true });

    const sizeObserver = new ResizeObserver(scheduleMeasure);
    sizeObserver.observe(table);

    scheduleRef.current = scheduleMeasure;
    scheduleMeasure();
    document.addEventListener('scroll', scheduleMeasure, { capture: true, passive: true });

    return () => {
      scheduleRef.current = null;
      document.removeEventListener('scroll', scheduleMeasure, { capture: true });
      rowsObserver.disconnect();
      sizeObserver.disconnect();
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [rowsSignature, overlay]);

  const isMeasured = isEnabled && !!overlay && measurement?.overlay === overlay;

  return {
    bands: isMeasured && measurement ? measurement.bands : EMPTY_BANDS,
    bandHeight: isMeasured && measurement ? measurement.bandHeight : STICKY_GROUP_ROW_HEIGHT,
  };
};

export default useStickyGroupBands;
